"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Buyerfaq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: "How do I start sourcing on KTEX?",
    answer: "Getting started is simple. Register your business account, complete the verification process, and gain immediate access to our global network of verified suppliers and real-time trade requests."
  }, {
    question: "What is the verification process for buyers?",
    answer: "We prioritize security and trust. Every buyer undergoes a standard business verification to ensure all trade participants are legitimate, protecting the integrity of our trading community."
  }, {
    question: "Can I request specific product quantities?",
    answer: "Yes. Our platform allows you to post detailed WTB (Want to Buy) requests, specifying exact quantities, quality standards, and delivery timelines to attract the right suppliers."
  }, {
    question: "How are payments handled?",
    answer: "KTEX facilitates secure transaction protocols. We provide structured payment frameworks that ensure transparency and safety for both buyers and suppliers throughout the procurement lifecycle."
  }, {
    question: "Do you support international shipping?",
    answer: "Absolutely. We connect you with suppliers capable of handling complex logistics, ensuring your goods are managed professionally from the point of origin to your final destination."
  }, {
    question: "How do I track my trade requests?",
    answer: "Your dashboard provides a centralized view of all active requests, supplier responses, and negotiation statuses, keeping you informed at every stage of the trade."
  }, {
    question: "Is there a fee to join the community?",
    answer: "Joining the KTEX trading community is free for qualified businesses. We operate on a transparent model where value is generated through successful, high-quality trade connections."
  }, {
    question: "What if I need custom sourcing support?",
    answer: "For specialized requirements, our dedicated account management team is available to assist you in finding niche suppliers and navigating complex procurement challenges."
  }, {
    question: "How do we begin our first trade?",
    answer: "Simply navigate to our 'Become a Buyer' page to register. Once approved, you can immediately start browsing categories or posting your first request to our supplier network."
  }, {
    question: "How is product availability and sourcing managed?",
    answer: "We maintain a dynamic network of verified suppliers across multiple regions. Our platform provides real-time updates on stock levels and sourcing capabilities to ensure your procurement needs are met without delay."
  }, {
    question: "What are the Minimum Order Quantities (MOQ)?",
    answer: "MOQs vary by supplier and product category. However, our platform allows you to filter suppliers based on your volume requirements, and you can negotiate specific quantities directly through our secure messaging system."
  }, {
    question: "What payment terms and options are available?",
    answer: "We support a variety of professional payment methods, including Letter of Credit (L/C), Telegraphic Transfer (T/T), and secure escrow services. Specific terms are finalized during the negotiation phase to ensure mutual protection."
  }, {
    question: "What are the typical delivery timelines and shipping methods?",
    answer: "Delivery timelines depend on the shipping method (Air, Sea, or Land) and the origin of the goods. Suppliers provide estimated lead times in their quotes, and we offer integrated logistics tracking for full transparency."
  }, {
    question: "What is the policy for returns and warranties?",
    answer: "All suppliers on KTEX are required to provide clear warranty terms. In the event of quality discrepancies, our dispute resolution framework ensures that returns and claims are handled professionally and fairly."
  }, {
    question: "How does the quote process and pricing work?",
    answer: "Once you post a request, suppliers submit detailed quotes. Pricing is transparent and includes breakdowns for unit costs, taxes, and shipping, allowing you to make informed comparisons and secure the best value."
  }, {
    question: "Are there specific benefits for bulk ordering?",
    answer: "Yes, bulk orders often qualify for tiered pricing discounts and priority production slots. Our account managers can also provide dedicated support to coordinate large-scale procurement projects and logistics."
  }];
  return <section data-section-id="13206" id="buyerfaq" className="bg-white py-32 md:py-44">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-4 md:col-start-2">
            <div ref={leftRef} className="md:sticky md:top-32 transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]" style={{
            opacity: leftVisible ? 1 : 0,
            transform: leftVisible ? "translateY(0)" : "translateY(12px)"
          }}>
              <p className="text-xs font-sans uppercase text-slate-600" style={{
              letterSpacing: "0.18em"
            }}>
                Buyer Support
              </p>

              <h2 className="mt-6 font-default font-semibold text-slate-900" style={{
              fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em"
            }}>
                Common questions, <em className="italic font-light">clarified</em>.
              </h2>

              <p className="mt-8 font-default text-slate-700" style={{
              fontSize: "clamp(0.95rem, 1vw, 1rem)",
              lineHeight: 1.65
            }}>
                Everything you need to know to navigate the KTEX marketplace with confidence.
              </p>
            </div>
          </div>

          <div className="md:col-span-6">
            <div ref={rightRef}>
              {faqs.map((item, i) => {
              const isOpen = openIndex === i;
              return <div key={i} data-index={i} className="border-t border-slate-200 last:border-b transition-all ease-[cubic-bezier(0.22,1,0.36,1)]" style={{
                opacity: rightVisible ? 1 : 0,
                transform: rightVisible ? "translateY(0)" : "translateY(12px)",
                transitionDuration: "900ms",
                transitionDelay: `${i * 60}ms`,
                transitionProperty: "opacity, transform"
              }}>
                    <button type="button" onClick={() => setOpenIndex(isOpen ? null : i)} aria-expanded={isOpen} className="w-full flex items-start justify-between gap-8 py-8 text-left group">
                      <span className="font-default font-medium text-slate-900" style={{
                    fontSize: "clamp(1.25rem, 2.4vw, 2.25rem)",
                    lineHeight: 1.15,
                    letterSpacing: "-0.01em"
                  }}>
                        {item.question}
                      </span>

                      <span className="shrink-0 mt-3 text-slate-500 transition-transform duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)]" style={{
                    transform: isOpen ? "rotate(45deg)" : "rotate(0deg)"
                  }} aria-hidden="true">
                        <Plus strokeWidth={1} className="size-6" />
                      </span>
                    </button>

                    <div className="overflow-hidden transition-all ease-[cubic-bezier(0.22,1,0.36,1)]" style={{
                  maxHeight: isOpen ? "32rem" : "0rem",
                  opacity: isOpen ? 1 : 0,
                  transitionDuration: "500ms"
                }}>
                      <p className="pb-8 pr-12 font-default text-slate-600 max-w-prose" style={{
                    fontSize: "clamp(0.95rem, 1vw, 1rem)",
                    lineHeight: 1.65
                  }}>
                        {item.answer}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}